import React, { useEffect, useState } from "react";
import { apiAdminProducts, apiAdminCreateProduct, apiAdminUpdateProduct, apiAdminDeleteProduct } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const empty = {
  sku: "", name: "", short: "", category: "peptides",
  tags: [], image: "", infoCardImage: "", coaUrl: "", casNumber: "",
  sequence: "", molecularWeight: "", molecularFormula: "", badge: "",
  variants: [{ size: "5mg", price: 0, compareAt: 0, stock: 0 }],
  description: "", benefits: [], rating: 4.8, reviews: 0, active: true,
};

const CATS = ["peptides", "performance", "fat-loss", "longevity", "bundles", "supplies"];

const AdminProducts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setItems(await apiAdminProducts());
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onSave = async (form) => {
    try {
      if (form.id) await apiAdminUpdateProduct(form.id, form);
      else await apiAdminCreateProduct(form);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await apiAdminDeleteProduct(id);
    toast.success("Deleted");
    load();
  };

  const filtered = items.filter((p) => (p.name + p.sku).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="h-px w-8 bg-[#b91c1c]" />
            <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Catalog</span>
          </div>
          <h1 className="font-display text-4xl font-semibold text-[#0a0a0a] tracking-tight">Products</h1>
        </div>
        <Button onClick={() => setEditing({ ...empty })} className="btn-primary rounded-none h-11 px-6 text-[12px] font-semibold uppercase tracking-[0.18em]">
          <Plus className="h-4 w-4 mr-2" /> New Product
        </Button>
      </div>

      <div className="relative max-w-sm mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or SKU…" className="pl-9 h-10 rounded-none" />
      </div>

      {loading ? <div className="text-sm text-gray-500">Loading…</div> : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fafbfc] border-b border-gray-200 text-[10px] uppercase tracking-[0.18em] text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold"></th>
                <th className="text-left px-4 py-3 font-semibold">Product</th>
                <th className="text-left px-4 py-3 font-semibold">SKU</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
                <th className="text-right px-4 py-3 font-semibold">Price</th>
                <th className="text-right px-4 py-3 font-semibold">Stock</th>
                <th className="text-right px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const v = p.variants[0] || {};
                const stock = p.variants.reduce((s, x) => s + (x.stock || 0), 0);
                return (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-[#fafbfc]">
                    <td className="px-4 py-3"><img src={p.image} alt="" className="h-10 w-10 object-contain bg-white border border-gray-100 p-1" /></td>
                    <td className="px-4 py-3 font-semibold text-[#0a0a0a]">{p.name}</td>
                    <td className="px-4 py-3 text-gray-600 font-mono text-xs">{p.sku}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{p.category}</td>
                    <td className="px-4 py-3 text-right font-semibold">${(v.price || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">{stock}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-[10px] font-semibold uppercase tracking-[0.18em] px-2 py-1 ${p.active ? "text-green-700 bg-green-100" : "text-gray-700 bg-gray-100"}`}>
                        {p.active ? "Active" : "Hidden"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-2">
                        <button onClick={() => setEditing(p)} className="text-gray-500 hover:text-[#0a0a0a]"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => onDelete(p.id)} className="text-gray-500 hover:text-[#b91c1c]"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSave={onSave} />}
    </div>
  );
};

const ProductEditor = ({ product, onClose, onSave }) => {
  const [form, setForm] = useState(product);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVariant = (idx, k, v) => setForm((f) => {
    const variants = [...f.variants];
    variants[idx] = { ...variants[idx], [k]: k === "size" ? v : Number(v) };
    return { ...f, variants };
  });
  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { size: "", price: 0, compareAt: 0, stock: 0 }] }));
  const removeVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 py-4 border-b border-gray-200">
          <DialogTitle className="font-display text-2xl font-semibold text-[#0a0a0a]">
            {form.id ? "Edit Product" : "New Product"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => set("sku", e.target.value)} required className="mt-1 rounded-none" /></div>
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1 rounded-none" /></div>
            <div className="col-span-2"><Label>Short Description</Label><Input value={form.short} onChange={(e) => set("short", e.target.value)} className="mt-1 rounded-none" /></div>
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1 w-full h-10 px-3 border border-gray-300 text-sm bg-white">
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><Label>Badge (optional)</Label><Input value={form.badge} onChange={(e) => set("badge", e.target.value)} placeholder="e.g. BEST SELLER" className="mt-1 rounded-none" /></div>
            <div className="col-span-2"><Label>Image URL</Label><Input value={form.image} onChange={(e) => set("image", e.target.value)} className="mt-1 rounded-none" /></div>
            <div className="col-span-2"><Label>Research Card Image URL (optional)</Label><Input value={form.infoCardImage} onChange={(e) => set("infoCardImage", e.target.value)} className="mt-1 rounded-none" /></div>
            <div className="col-span-2">
              <Label>COA PDF URL (optional)</Label>
              <Input
                value={form.coaUrl || ""}
                onChange={(e) => set("coaUrl", e.target.value)}
                placeholder="https://… link to Certificate of Analysis PDF"
                className="mt-1 rounded-none"
                data-testid="admin-product-coa-url-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">Leave empty to display "COA — Coming Soon" badge on the product page.</p>
            </div>
            <div className="col-span-2">
              <Label>CAS Number (optional)</Label>
              <Input
                value={form.casNumber || ""}
                onChange={(e) => set("casNumber", e.target.value)}
                placeholder="e.g. 137525-51-0"
                className="mt-1 rounded-none font-mono"
                data-testid="admin-product-cas-number-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">CAS registry number. Leave blank for blends or supplies that have no single CAS.</p>
            </div>
            <div>
              <Label>Molecular Weight (optional)</Label>
              <Input
                value={form.molecularWeight || ""}
                onChange={(e) => set("molecularWeight", e.target.value)}
                placeholder="e.g. 1419.55 g/mol"
                className="mt-1 rounded-none font-mono"
                data-testid="admin-product-mw-input"
              />
            </div>
            <div>
              <Label>Molecular Formula (optional)</Label>
              <Input
                value={form.molecularFormula || ""}
                onChange={(e) => set("molecularFormula", e.target.value)}
                placeholder="e.g. C62H98N16O22"
                className="mt-1 rounded-none font-mono"
                data-testid="admin-product-mf-input"
              />
            </div>
            <div className="col-span-2">
              <Label>Peptide Sequence (optional, one-letter code)</Label>
              <Textarea
                rows={2}
                value={form.sequence || ""}
                onChange={(e) => set("sequence", e.target.value)}
                placeholder="e.g. GEPPPGKPADDAGLV"
                className="mt-1 rounded-none font-mono"
                data-testid="admin-product-sequence-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">Leave blank for non-peptides (small molecules, blends, supplies).</p>
            </div>
            <div className="col-span-2"><Label>Description</Label><Textarea rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1 rounded-none" /></div>
            <div className="col-span-2"><Label>Benefits (one per line)</Label><Textarea rows={3} value={(form.benefits || []).join("\n")} onChange={(e) => set("benefits", e.target.value.split("\n").filter(Boolean))} className="mt-1 rounded-none" /></div>
            <div><Label>Tags (comma separated)</Label><Input value={(form.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className="mt-1 rounded-none" /></div>
            <div><Label>Active</Label>
              <select value={form.active ? "yes" : "no"} onChange={(e) => set("active", e.target.value === "yes")} className="mt-1 w-full h-10 px-3 border border-gray-300 text-sm bg-white">
                <option value="yes">Visible</option>
                <option value="no">Hidden</option>
              </select>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <Label>Variants</Label>
              <button type="button" onClick={addVariant} className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0a0a0a] hover:text-[#b91c1c]">+ Add variant</button>
            </div>
            <div className="space-y-2">
              {form.variants.map((v, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-end">
                  <div><Label className="text-[10px]">Size</Label><Input value={v.size} onChange={(e) => setVariant(idx, "size", e.target.value)} className="mt-1 rounded-none" /></div>
                  <div><Label className="text-[10px]">Price</Label><Input type="number" step="0.01" value={v.price} onChange={(e) => setVariant(idx, "price", e.target.value)} className="mt-1 rounded-none" /></div>
                  <div><Label className="text-[10px]">Compare</Label><Input type="number" step="0.01" value={v.compareAt || ""} onChange={(e) => setVariant(idx, "compareAt", e.target.value)} className="mt-1 rounded-none" /></div>
                  <div><Label className="text-[10px]">Stock</Label><Input type="number" value={v.stock} onChange={(e) => setVariant(idx, "stock", e.target.value)} className="mt-1 rounded-none" /></div>
                  {form.variants.length > 1 && (
                    <button type="button" onClick={() => removeVariant(idx)} className="text-[#b91c1c] hover:text-red-700 h-10 px-2"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none h-11 px-6 text-[12px] uppercase tracking-[0.18em]">Cancel</Button>
            <Button type="submit" className="btn-primary rounded-none h-11 px-6 text-[12px] font-semibold uppercase tracking-[0.18em]">Save</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminProducts;
